// src/hooks/useTenantBoundary.js
import { useEffect, useState, useCallback, useRef } from 'react'
import { fetchTenantBoundary } from '../services/tenantService'

const TENANT_BOUNDARY_SOURCE = 'tenant-boundary-source'
const TENANT_BOUNDARY_LINE = 'tenant-boundary-line'
const TENANT_BOUNDARY_FILL = 'tenant-boundary-fill'
const TENANT_BOUNDARY_SESSION_PREFIX = 'ArbolEc_tenantBoundary:'

// Cache compartido entre montajes del hook (misma sesión de la app)
const tenantBoundaryMemoryCache = new Map()
const tenantBoundaryRequestCache = new Map()

const getSessionBoundaryKey = slug => `${TENANT_BOUNDARY_SESSION_PREFIX}${slug}`

const readBoundaryFromSession = slug => {
  if (!slug || typeof window === 'undefined') return null

  try {
    const raw = window.sessionStorage.getItem(getSessionBoundaryKey(slug))
    if (!raw) return null
    const parsed = JSON.parse(raw)
    return parsed?.type ? parsed : null
  } catch {
    return null
  }
}

const writeBoundaryToSession = (slug, geojson) => {
  if (!slug || !geojson?.type || typeof window === 'undefined') return

  try {
    window.sessionStorage.setItem(getSessionBoundaryKey(slug), JSON.stringify(geojson))
  } catch {
    // Ignorar límites de cuota en sessionStorage
  }
}

/**
 * Hook para gestionar el boundary de un tenant seleccionado
 * - Carga el GeoJSON del boundary desde la API
 * - Añade capas visuales al mapa
 * - Aplica restricción de bounds para navegación
 *
 * @param {Object} map - Instancia del mapa de Mapbox
 * @param {boolean} mapLoaded - Estado de carga del mapa
 * @param {Object} options - Opciones de configuración
 * @param {string} options.selectedTenant - Slug del tenant seleccionado (null = ninguno)
 * @param {Array} options.tenantBounds - Bounds del tenant [[sw_lng, sw_lat], [ne_lng, ne_lat]]
 * @param {Array} options.cantonBounds - Bounds del cantón (fallback cuando no hay tenant)
 * @param {boolean} options.isAdmin - Si es admin, no aplicar restricciones
 * @param {boolean} options.initialVisible - Visibilidad inicial al añadir capas
 * @param {Object} options.style - Estilos personalizados para las capas
 */
export const useTenantBoundary = (map, mapLoaded, options = {}) => {
  const {
    selectedTenant = null,
    tenantBounds = null,
    cantonBounds = null,
    isAdmin = false,
    initialVisible = true,
    style = {},
  } = options

  const {
    lineColor = '#1976d2',
    lineWidth = 3,
    fillColor = '#1976d2',
    fillOpacity = 0.08,
    beforeLayer = 'clusters',
  } = style

  const [boundaryData, setBoundaryData] = useState(null)
  const [isGeneric, setIsGeneric] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [isVisible, setIsVisible] = useState(false)

  const previousTenantRef = useRef(null)
  const selectedTenantRef = useRef(selectedTenant)
  const layersAddedRef = useRef(false)
  const requestCounterRef = useRef(0)

  useEffect(() => {
    selectedTenantRef.current = selectedTenant
  }, [selectedTenant])

  const hasStyle = useCallback(() => {
    if (!map) return false
    try {
      return Boolean(map.getStyle?.())
    } catch {
      return false
    }
  }, [map])

  const ensureBoundaryOrder = useCallback(
    (retries = 40) => {
      if (!map || !hasStyle()) return

      const referenceLayer = map.getLayer(beforeLayer)
      if (!referenceLayer) {
        if (retries > 0) {
          setTimeout(() => ensureBoundaryOrder(retries - 1), 100)
        }
        return
      }

      try {
        if (map.getLayer(TENANT_BOUNDARY_FILL)) {
          map.moveLayer(TENANT_BOUNDARY_FILL, beforeLayer)
        }
        if (map.getLayer(TENANT_BOUNDARY_LINE)) {
          map.moveLayer(TENANT_BOUNDARY_LINE, beforeLayer)
        }
      } catch (err) {
        if (retries > 0) {
          setTimeout(() => ensureBoundaryOrder(retries - 1), 100)
        }
      }
    },
    [map, beforeLayer, hasStyle]
  )

  // Limpiar capas del mapa
  const cleanupLayers = useCallback(() => {
    if (!map || !hasStyle()) {
      layersAddedRef.current = false
      return
    }

    try {
      if (map.getLayer(TENANT_BOUNDARY_LINE)) {
        map.removeLayer(TENANT_BOUNDARY_LINE)
      }
      if (map.getLayer(TENANT_BOUNDARY_FILL)) {
        map.removeLayer(TENANT_BOUNDARY_FILL)
      }
      if (map.getSource(TENANT_BOUNDARY_SOURCE)) {
        map.removeSource(TENANT_BOUNDARY_SOURCE)
      }
      layersAddedRef.current = false
    } catch (err) {
      console.warn('[TenantBoundary] Error limpiando capas:', err)
    }
  }, [map, hasStyle])

  // Añadir capas al mapa
  const addBoundaryLayers = useCallback(
    geojson => {
      if (!map || !geojson || !hasStyle()) return

      cleanupLayers()

      try {
        // Verificar si existe la capa de referencia
        const hasBeforeLayer = map.getLayer(beforeLayer)

        // Añadir source
        map.addSource(TENANT_BOUNDARY_SOURCE, {
          type: 'geojson',
          data: geojson,
        })

        // Añadir capa de relleno
        map.addLayer(
          {
            id: TENANT_BOUNDARY_FILL,
            type: 'fill',
            slot: 'top',
            source: TENANT_BOUNDARY_SOURCE,
            layout: { visibility: initialVisible ? 'visible' : 'none' },
            paint: {
              'fill-color': fillColor,
              'fill-opacity': fillOpacity,
            },
          },
          hasBeforeLayer ? beforeLayer : undefined
        )

        // Añadir capa de línea
        map.addLayer(
          {
            id: TENANT_BOUNDARY_LINE,
            type: 'line',
            slot: 'top',
            source: TENANT_BOUNDARY_SOURCE,
            layout: { visibility: initialVisible ? 'visible' : 'none' },
            paint: {
              'line-color': lineColor,
              'line-width': lineWidth,
              'line-dasharray': [2, 2],
            },
          },
          hasBeforeLayer ? beforeLayer : undefined
        )

        ensureBoundaryOrder()
        layersAddedRef.current = true
        setIsVisible(initialVisible)
      } catch (err) {
        console.error('[TenantBoundary] Error añadiendo capas:', err)
        setError('Error al mostrar el límite del tenant')
      }
    },
    [
      map,
      cleanupLayers,
      beforeLayer,
      lineColor,
      lineWidth,
      fillColor,
      fillOpacity,
      initialVisible,
      ensureBoundaryOrder,
      hasStyle,
    ]
  )

  const getBoundaryGeojson = useCallback(async (tenantSlug, { force = false } = {}) => {
    if (!tenantSlug) return null

    if (!force) {
      const memoryGeojson = tenantBoundaryMemoryCache.get(tenantSlug)
      if (memoryGeojson?.type || memoryGeojson?.es_generico) return memoryGeojson

      const sessionGeojson = readBoundaryFromSession(tenantSlug)
      if (sessionGeojson?.type) {
        tenantBoundaryMemoryCache.set(tenantSlug, sessionGeojson)
        return sessionGeojson
      }
    }

    if (tenantBoundaryRequestCache.has(tenantSlug)) {
      return tenantBoundaryRequestCache.get(tenantSlug)
    }

    const request = fetchTenantBoundary(tenantSlug, {
      noCache: force,
      throwOnError: true,
    })
      .then(geojson => {
        if (geojson?.es_generico) {
          tenantBoundaryMemoryCache.set(tenantSlug, { es_generico: true })
          return { es_generico: true }
        }
        if (geojson?.type) {
          tenantBoundaryMemoryCache.set(tenantSlug, geojson)
          writeBoundaryToSession(tenantSlug, geojson)
          return geojson
        }
        return null
      })
      .finally(() => {
        tenantBoundaryRequestCache.delete(tenantSlug)
      })

    tenantBoundaryRequestCache.set(tenantSlug, request)
    return request
  }, [])

  const loadBoundary = useCallback(
    async (tenantSlug, { force = false } = {}) => {
      if (!tenantSlug) return null

      const requestId = ++requestCounterRef.current
      setLoading(true)
      setError(null)

      try {
        const geojson = await getBoundaryGeojson(tenantSlug, { force })
        const isStale =
          requestId !== requestCounterRef.current || tenantSlug !== selectedTenantRef.current
        if (isStale) return null

        if (geojson?.es_generico) {
          setIsGeneric(true)
          setBoundaryData(null)
          setIsVisible(false)
          cleanupLayers()
          return null
        }

        if (geojson?.type) {
          setIsGeneric(false)
          setBoundaryData(geojson)
          addBoundaryLayers(geojson)
          return geojson
        }

        setIsGeneric(false)
        setBoundaryData(null)
        setIsVisible(false)
        cleanupLayers()
        setError('No se encontró boundary para el tenant seleccionado')
        return null
      } catch {
        const isStale =
          requestId !== requestCounterRef.current || tenantSlug !== selectedTenantRef.current
        if (isStale) return null

        setError('Error al cargar el límite del tenant')
        setBoundaryData(null)
        setIsVisible(false)
        cleanupLayers()
        return null
      } finally {
        if (requestId === requestCounterRef.current) {
          setLoading(false)
        }
      }
    },
    [addBoundaryLayers, cleanupLayers, getBoundaryGeojson]
  )

  // Cambiar visibilidad de las capas
  const setVisibility = useCallback(
    visible => {
      // Si no están listas las capas y el usuario quiere mostrar, reintentar carga.
      // No reintentar si ya hubo un error (ej. 404): evita bucle infinito de peticiones.
      if (visible && selectedTenant && !layersAddedRef.current) {
        if (boundaryData?.type) {
          addBoundaryLayers(boundaryData)
        } else if (!loading && !error) {
          void loadBoundary(selectedTenant, { force: true })
        }
        return
      }

      if (!map || !layersAddedRef.current || !hasStyle()) {
        setIsVisible(false)
        return
      }

      const visibility = visible ? 'visible' : 'none'

      try {
        let hasRenderableLayer = false

        if (map.getLayer(TENANT_BOUNDARY_LINE)) {
          map.setLayoutProperty(TENANT_BOUNDARY_LINE, 'visibility', visibility)
          hasRenderableLayer = true
        }
        if (map.getLayer(TENANT_BOUNDARY_FILL)) {
          map.setLayoutProperty(TENANT_BOUNDARY_FILL, 'visibility', visibility)
          hasRenderableLayer = true
        }

        if (!hasRenderableLayer) {
          setIsVisible(false)
          if (visible && selectedTenant && !loading && !error) {
            void loadBoundary(selectedTenant, { force: true })
          }
          return
        }

        setIsVisible(visible)
      } catch (err) {
        console.warn('[TenantBoundary] Error cambiando visibilidad:', err)
        setIsVisible(false)
      }
    },
    [map, hasStyle, selectedTenant, boundaryData, loading, error, addBoundaryLayers, loadBoundary]
  )

  // Toggle visibilidad
  const toggleVisibility = useCallback(() => {
    setVisibility(!isVisible)
  }, [setVisibility, isVisible])

  // Cargar boundary cuando cambia el tenant
  useEffect(() => {
    if (!map || !mapLoaded) return

    // Si no hay tenant seleccionado, limpiar
    if (!selectedTenant) {
      requestCounterRef.current += 1
      setLoading(false)
      setError(null)
      setIsGeneric(false)
      cleanupLayers()
      setBoundaryData(null)
      setIsVisible(false)
      previousTenantRef.current = null

      // Restaurar bounds del cantón si no es admin
      if (!isAdmin && cantonBounds) {
        try {
          map.setMaxBounds(cantonBounds)
        } catch (err) {
          console.warn('[TenantBoundary] Error restaurando bounds:', err)
        }
      } else if (isAdmin) {
        map.setMaxBounds(null)
      }

      return
    }

    // Si es el mismo tenant, no recargar
    if (selectedTenant === previousTenantRef.current) return

    // Limpiar boundary anterior INMEDIATAMENTE para evitar validar contra datos obsoletos
    previousTenantRef.current = selectedTenant
    setBoundaryData(null)
    setIsGeneric(false)
    setIsVisible(false)
    setError(null)
    cleanupLayers()

    void loadBoundary(selectedTenant)
  }, [map, mapLoaded, selectedTenant, loadBoundary, cleanupLayers, isAdmin, cantonBounds])

  // Aplicar bounds del tenant para restringir navegación
  useEffect(() => {
    if (!map || !mapLoaded || isAdmin) return

    if (selectedTenant && tenantBounds && tenantBounds[0] && tenantBounds[0][0]) {
      // tenantBounds viene como [[[sw_lng, sw_lat], [ne_lng, ne_lat]]]
      // Necesitamos extraer el array interno
      const bounds = Array.isArray(tenantBounds[0][0]) ? tenantBounds[0] : tenantBounds

      if (bounds && bounds.length === 2) {
        try {
          map.setMaxBounds(bounds)
        } catch (err) {
          console.warn('[TenantBoundary] Error aplicando bounds del tenant:', err)
        }
      }
    }
  }, [map, mapLoaded, selectedTenant, tenantBounds, isAdmin])

  // Cleanup al desmontar
  useEffect(() => {
    return () => {
      requestCounterRef.current += 1
      cleanupLayers()
    }
  }, [cleanupLayers])

  const retryBoundaryLoad = useCallback(() => {
    if (!selectedTenant || loading) return Promise.resolve(null)
    return loadBoundary(selectedTenant, { force: true })
  }, [selectedTenant, loading, loadBoundary])

  return {
    boundaryData,
    isGeneric,
    loading,
    error,
    isVisible,
    setVisibility,
    toggleVisibility,
    retryBoundaryLoad,
    hasBoundary: Boolean(boundaryData),
  }
}
